import React, { useRef, useState } from 'react';
import {
  Download,
  FileText,
  Paperclip,
  Trash2,
  Upload,
} from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { ActionModal } from './ActionModal';
import {
  useAnexos,
  useEnviarAnexo,
  useRemoverAnexo,
} from '../hooks';
import { urlDownloadAnexo } from '../api';
import type { OpportunityAttachment } from '../api';
import { formatDateTimeBR } from '../utils/format';

interface Props {
  opportunityId: string;
  canEdit: boolean;
}

/**
 * Seção de anexos (US-COM-004 §Anexos). Upload via FormData multipart;
 * download navega direto para a URL autenticada. Remove disparado com
 * `ConfirmDialog` — o backend bloqueia remoção se o anexo estiver
 * referenciado como evidência de ganho.
 */
export const OpportunityAttachmentsSection: React.FC<Props> = ({
  opportunityId,
  canEdit,
}) => {
  const { data, isLoading, isError, error } = useAnexos(opportunityId);
  const remove = useRemoverAnexo(opportunityId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<OpportunityAttachment | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  return (
    <section className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Anexos
        </h2>
        {canEdit && (
          <button
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
          >
            <Upload className="h-3 w-3" />
            Enviar anexo
          </button>
        )}
      </div>

      {rowError && (
        <Alert variant="error" className="mb-2">
          {rowError}
        </Alert>
      )}

      {isLoading && <Skeleton className="h-16 w-full" />}
      {isError && (
        <Alert variant="error">
          Erro ao carregar anexos: {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {data && data.length === 0 && (
        <p className="text-sm text-gray-500">
          Nenhum anexo. Envie evidências, apresentações ou contratos para consolidar
          a documentação da oportunidade.
        </p>
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {data.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 py-2">
              <div className="min-w-0 flex-1">
                <a
                  href={urlDownloadAnexo(opportunityId, a.id)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"
                >
                  <FileText className="h-4 w-4" />
                  {a.filename}
                </a>
                <div className="text-xs text-gray-500">
                  {a.kind && <span className="mr-1 rounded bg-gray-100 px-1">{a.kind}</span>}
                  {formatSize(a.sizeBytes)}
                  {a.isReusableOnProposal && (
                    <span className="ml-1 rounded bg-green-100 px-1 text-green-800">
                      Reutilizável na proposta
                    </span>
                  )}
                  <span className="ml-1">Enviado em {formatDateTimeBR(a.uploadedAt)}</span>
                </div>
                {a.description && (
                  <p className="mt-0.5 text-xs text-gray-600">{a.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <a
                  href={urlDownloadAnexo(opportunityId, a.id)}
                  className="rounded p-1 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                  title="Baixar"
                  aria-label="Baixar anexo"
                >
                  <Download className="h-4 w-4" />
                </a>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setRowError(null);
                      setPendingDelete(a);
                    }}
                    className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Remover"
                    aria-label="Remover anexo"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {uploadOpen && (
        <UploadAttachmentModal
          opportunityId={opportunityId}
          onClose={() => setUploadOpen(false)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title="Remover anexo"
          message={`Remover "${pendingDelete.filename}"? Se este anexo for a evidência do desfecho Ganha, o backend rejeita a operação.`}
          confirmLabel="Remover"
          danger
          busy={remove.isPending}
          onConfirm={async () => {
            try {
              await remove.mutateAsync(pendingDelete.id);
              setPendingDelete(null);
            } catch (e) {
              setRowError(getFriendlyErrorMessage(e, 'Falha ao remover anexo.'));
              setPendingDelete(null);
            }
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </section>
  );
};

const UploadAttachmentModal: React.FC<{
  opportunityId: string;
  onClose: () => void;
}> = ({ opportunityId, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState('');
  const [description, setDescription] = useState('');
  const [isReusable, setIsReusable] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useEnviarAnexo(opportunityId);

  const submit = async () => {
    setErr(null);
    if (!file) {
      setErr('Selecione um arquivo.');
      return;
    }
    try {
      await upload.mutateAsync({
        file,
        meta: {
          kind: kind.trim() || undefined,
          description: description.trim() || undefined,
          isReusableOnProposal: isReusable || undefined,
        },
      });
      onClose();
    } catch (e) {
      setErr(getFriendlyErrorMessage(e, 'Falha ao enviar anexo.'));
    }
  };

  return (
    <ActionModal
      isOpen
      onClose={onClose}
      onSubmit={submit}
      title="Enviar anexo"
      description="Formatos permitidos: PDF, PNG, JPEG, DOC/DOCX, XLS/XLSX, XML. Limite 20 MB."
      submitLabel="Enviar"
      submitVariant="primary"
      isSubmitting={upload.isPending}
      submitError={err}
      canSubmit={!!file}
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            Arquivo
          </label>
          <input
            ref={inputRef}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.xml"
            className="block w-full text-sm text-gray-600 file:mr-2 file:rounded-md file:border file:border-gray-300 file:bg-gray-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-gray-700 hover:file:bg-gray-100"
          />
          {file && (
            <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <Paperclip className="h-3 w-3" />
              {file.name} — {formatSize(String(file.size))}
            </p>
          )}
        </div>
        <TextInput
          label="Tipo lógico (opcional — ex.: ata, edital, evidência)"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        />
        <Textarea
          label="Descrição (opcional)"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isReusable}
            onChange={(e) => setIsReusable(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Reutilizar automaticamente no snapshot da proposta gerada
        </label>
      </div>
    </ActionModal>
  );
};

function formatSize(bytes: string | null): string {
  if (!bytes) return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n === 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
